<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <h1 id="primary-heading" class="sr-only">Environment Segments</h1>
            <div class="px-4 sm:px-6 lg:px-8">
                <div
                    v-if="!loading && hasResults"
                    class="sm:flex sm:items-center"
                >
                    <div class="sm:flex sm:space-x-4">
                        <h1 class="text-2xl font-medium text-taupe-800">
                            Segments
                        </h1>
                    </div>
                </div>

                <zen-loading-indicator v-if="loading" />

                <div v-if="!loading && hasResults" class="mt-8">
                    <zen-grid>
                        <zen-grid-table>
                            <zen-grid-header>
                                <zen-grid-header-row>
                                    <zen-grid-header-cell
                                        :hidable="false"
                                        title="Name"
                                    />
                                    <zen-grid-header-cell
                                        alignment="center"
                                        title="Count"
                                    />
                                    <zen-grid-header-cell
                                        alignment="center"
                                        title="Actions"
                                    />
                                </zen-grid-header-row>
                            </zen-grid-header>
                            <zen-grid-body
                                :results="filteredResults"
                                :loading="loading"
                            >
                                <zen-grid-body-row
                                    v-for="result in filteredResults"
                                    :key="result.ulid"
                                >
                                    <zen-grid-body-cell
                                        :hidable="false"
                                        :primary="true"
                                    >
                                        <zen-grid-body-cell-primary-link
                                            :to="{
                                                name: 'ProjectSegmentDetailScreen',
                                                params: {
                                                    projectKey:
                                                        currentEnvironment.projectKey,
                                                    environmentKey:
                                                        currentEnvironment.environmentKey,
                                                    segmentUlid: result.ulid,
                                                },
                                            }"
                                        >
                                            {{ result.name }}
                                        </zen-grid-body-cell-primary-link>

                                        <zen-grid-mobile-detail>
                                            <zen-grid-mobile-detail-item
                                                label="Count"
                                                :value="
                                                    String(result.count ?? 0)
                                                "
                                            />
                                            <zen-grid-mobile-detail-item
                                                label="Actions"
                                            >
                                                <zen-button
                                                    v-if="
                                                        can(
                                                            PERMISSIONS.SEGMENT_UPDATE,
                                                            permissionScope
                                                        )
                                                    "
                                                    variant="link"
                                                    @click="
                                                        openUpdateModal(result)
                                                    "
                                                >
                                                    Update
                                                </zen-button>
                                                <zen-button
                                                    v-if="
                                                        can(
                                                            PERMISSIONS.SEGMENT_DELETE,
                                                            permissionScope
                                                        )
                                                    "
                                                    variant="link-danger"
                                                    class="ml-3"
                                                    @click="
                                                        openRemoveModal(
                                                            result.ulid
                                                        )
                                                    "
                                                >
                                                    Remove
                                                </zen-button>
                                            </zen-grid-mobile-detail-item>
                                        </zen-grid-mobile-detail>
                                    </zen-grid-body-cell>
                                    <zen-grid-body-cell alignment="center">
                                        {{ result.count ?? 0 }}
                                    </zen-grid-body-cell>
                                    <zen-grid-body-cell alignment="center">
                                        <zen-table-icon
                                            v-if="
                                                can(
                                                    PERMISSIONS.SEGMENT_UPDATE,
                                                    permissionScope
                                                )
                                            "
                                            @click="openUpdateModal(result)"
                                            :srText="
                                                'Update ' +
                                                result.name +
                                                ' Segment'
                                            "
                                            tooltipText="Update"
                                        >
                                            <zen-icon-update />
                                        </zen-table-icon>
                                        <zen-table-icon
                                            v-if="
                                                can(
                                                    PERMISSIONS.SEGMENT_DELETE,
                                                    permissionScope
                                                )
                                            "
                                            @click="
                                                openRemoveModal(result.ulid)
                                            "
                                            :srText="
                                                'Remove ' +
                                                result.name +
                                                ' Segment'
                                            "
                                            tooltipText="Remove"
                                        >
                                            <zen-icon-remove />
                                        </zen-table-icon>
                                    </zen-grid-body-cell>
                                </zen-grid-body-row>
                            </zen-grid-body>
                        </zen-grid-table>
                        <template v-slot:filters>
                            <input
                                type="search"
                                id="segment-search"
                                name="segment-search"
                                v-model="filters.search"
                                autocomplete="off"
                                class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-taupe-300 focus:ring-taupe-500 sm:text-sm lg:text-base"
                                placeholder="Filter segments by name..."
                                aria-label="Filter segments by name"
                            />
                        </template>
                        <template v-slot:actions>
                            <zen-button
                                v-if="
                                    can(
                                        PERMISSIONS.SEGMENT_CREATE,
                                        permissionScope
                                    )
                                "
                                variant="primary"
                                size="large"
                                @click="openCreateModal()"
                            >
                                Create segment
                            </zen-button>
                        </template>
                    </zen-grid>
                </div>
                <zen-empty-state
                    v-if="!loading && !hasResults"
                    title="No segments yet"
                    description="Segments let you group contexts together so you can target them as a unit in your flag rules. Create your first segment to get started."
                    :primary-action="
                        can(PERMISSIONS.SEGMENT_CREATE, permissionScope)
                            ? {
                                  label: 'Create segment',
                                  handler: openCreateModal,
                              }
                            : null
                    "
                >
                    <template #icon>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1"
                            stroke="currentColor"
                            class="h-20 w-20"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
                            />
                        </svg>
                    </template>
                </zen-empty-state>
                <zen-empty-table-message
                    v-else-if="!loading && hasResults && !hasFilteredResults"
                    title="No matching segments"
                    icon="ZenIconFilter"
                >
                    No segments match your filter.
                </zen-empty-table-message>
            </div>
        </section>

        <remove-segment-modal
            :is-open="modals.remove.show"
            @close="toggleRemoveModal"
            @confirm="confirmRemove"
        />

        <create-segment-slide-in
            :is-open="modals.create.show"
            v-model:name="modals.create.data.name"
            :error="modals.create.error"
            @close="toggleCreateModal"
            @confirm="confirmCreate"
        />

        <update-segment-slide-in
            :is-open="modals.update.show"
            v-model:name="modals.update.data.name"
            :error="modals.update.error"
            @close="toggleUpdateModal"
            @confirm="confirmUpdate"
        />
    </main>
</template>

<script>
import SegmentService from '@/services/segment.service'
import RemoveSegmentModal from '@/screens/trusted/project/modals/RemoveSegmentModal.vue'
import CreateSegmentSlideIn from '@/screens/trusted/project/modals/CreateSegmentSlideIn.vue'
import UpdateSegmentSlideIn from '@/screens/trusted/project/modals/UpdateSegmentSlideIn.vue'

import { errorMessage } from '@/utils/notifications'
import { removeBy, upsertBy } from '@/utils/list-mutations'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'

export default {
    name: 'ProjectSegmentsScreen',
    components: {
        RemoveSegmentModal,
        CreateSegmentSlideIn,
        UpdateSegmentSlideIn,
    },
    setup() {
        const { can } = usePermissions()
        const { current: currentEnvironment } = useCurrentEnvironment()
        return { can, PERMISSIONS, currentEnvironment }
    },
    computed: {
        permissionScope() {
            return {
                projectUlid: this.currentEnvironment.projectUlid,
                environmentUlid: this.currentEnvironment.environmentUlid,
            }
        },
        hasResults() {
            return this.results != null && this.results.length > 0
        },
        filteredResults() {
            if (this.results == null) {
                return []
            }

            const search = this.filters.search.trim().toLowerCase()
            if (search === '') {
                return this.results
            }

            return this.results.filter((result) =>
                (result.name || '').toLowerCase().includes(search)
            )
        },
        hasFilteredResults() {
            return this.filteredResults.length > 0
        },
    },
    data() {
        return {
            loading: true,
            results: [],
            filters: {
                search: '',
            },
            modals: {
                create: {
                    error: null,
                    show: false,
                    data: {
                        name: null,
                    },
                },
                remove: {
                    show: false,
                    ulid: null,
                },
                update: {
                    ulid: null,
                    error: null,
                    show: false,
                    data: {
                        name: null,
                    },
                },
            },
        }
    },
    methods: {
        confirmCreate() {
            SegmentService.create(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.modals.create.data
            ).then(
                (data) => {
                    if (this.results == null) {
                        this.results = []
                    }

                    this.results.push(data.data)
                    this.toggleCreateModal()
                },
                (error) => {
                    this.modals.create.error = error
                }
            )
        },
        confirmRemove() {
            SegmentService.remove(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.modals.remove.ulid
            ).then(
                () => {
                    this.results = removeBy(
                        this.results,
                        this.modals.remove.ulid
                    )
                    this.toggleRemoveModal()
                },
                (error) => {
                    errorMessage(error)
                }
            )
        },
        confirmUpdate() {
            SegmentService.update(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey,
                this.modals.update.ulid,
                this.modals.update.data
            ).then(
                (data) => {
                    this.results = upsertBy(this.results, data.data)
                    this.toggleUpdateModal()
                },
                (error) => {
                    this.modals.update.error = error
                }
            )
        },
        openCreateModal() {
            this.toggleCreateModal()
        },
        openRemoveModal(id) {
            this.modals.remove.ulid = id
            this.toggleRemoveModal()
        },
        openUpdateModal(segment) {
            this.modals.update.ulid = segment.ulid
            this.modals.update.data.name = segment.name
            this.toggleUpdateModal()
        },
        refresh() {
            this.loading = true
            SegmentService.list(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey
            ).then(
                (data) => {
                    this.results = data.data
                    this.loading = false
                },
                (error) => {
                    errorMessage(error)
                    this.loading = false
                }
            )
        },
        toggleCreateModal() {
            this.modals.create.show = !this.modals.create.show
            if (!this.modals.create.show) {
                this.modals.create.data.name = null
                this.modals.create.error = null
            }
        },
        toggleRemoveModal() {
            this.modals.remove.show = !this.modals.remove.show
            if (!this.modals.remove.show) {
                this.modals.remove.ulid = null
            }
        },
        toggleUpdateModal() {
            this.modals.update.show = !this.modals.update.show
            if (!this.modals.update.show) {
                this.modals.update.ulid = null
                this.modals.update.data.name = null
                this.modals.update.error = null
            }
        },
    },
    mounted() {
        this.refresh()
    },
}
</script>
